import {useCallback, useEffect, useMemo} from 'react';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs, {Dayjs} from 'dayjs';
import {useSelector} from 'react-redux';
import {DatePicker} from '@mui/x-date-pickers';
import OutlinedRadioControlLabel from '../../../../../components/OutlinedRadioControlLabel';
import {ITimedMessageDayType} from '../../../../../dto/timed-message/TimedMessageRequest.dto';
import TimedMsgDateRange from './TimedMsgDateRange';
import IconDeleteButton from '../../../../../components/Button/IconDeletebutton';
import {isInternalSelector} from '../../../../../store/Slices/userSlice';
import CustomCalIcon from '../../../../../asset/icons/icon_calendar_gray.svg';
import {iTimedMsgDateRange} from '../TimedMessageRequest';
import {GetDaysFromDateRange} from '../../../../../components/utlis/date-time/GetDaysInDateRange';

type Props = {
  dayType: string;
  onChange: (val: ITimedMessageDayType) => void;
  dates: iTimedMsgDateRange[];
  setDateRanges: React.Dispatch<React.SetStateAction<iTimedMsgDateRange[]>>;
  setFooterText?: React.Dispatch<React.SetStateAction<string>>;
  isToday?: boolean;
};

const MAX_ITEMS = 10;

const options = [
  {
    value: 'no-end',
    label: 'Start date only',
    caption: 'No end date for this message',
  },
  {
    value: 'one-day',
    label: 'One Day',
    caption: 'This message will play only during the selected dates',
  },
  {
    value: 'multiple',
    label: 'Multiple Days',
    caption: 'Multiple combinations of start date & end date',
  },
];

function MuiIcon() {
  return <img src={CustomCalIcon} alt='Date picker opening icon' width={23} />;
}

const TimedMsgRequestDays = ({
  dayType,
  onChange,
  dates,
  setDateRanges,
  setFooterText,
  isToday = false,
}: Props) => {
  const isStaff = useSelector(isInternalSelector);
  const minDate = isStaff ? dayjs() : dayjs().add(1, 'day');

  const handleTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDayType = event.target.value as ITimedMessageDayType;
      onChange(newDayType);
    },
    [onChange],
  );

  const handleRemove = (id: number) => {
    setDateRanges(prevDateRanges => {
      const newDateRanges = [...prevDateRanges];
      const index = newDateRanges.findIndex(o => o.id === id);
      if (index !== -1) {
        newDateRanges.splice(index, 1);
      }
      return newDateRanges;
    });
  };

  const handleNoEndDate = useCallback(
    (date: Dayjs | null) => {
      setDateRanges([
        {
          id: -1 * Date.now(),
          start_date: date,
          end_date: dayjs('2099-12-31'),
        },
      ]);
    },
    [setDateRanges],
  );

  useEffect(() => {
    if (dayType === 'multiple') {
      setFooterText?.(
        ` Up to ${MAX_ITEMS} date range can be added for each request`,
      );
    }
    return () => {
      setFooterText?.('');
    };
  }, [setFooterText, dayType]);

  return (
    <>
      <Typography variant='h5' aria-level={2} className='mb-2'>
        Select Date
      </Typography>
      <Typography
        variant='subtitle1'
        aria-level={2}
        className='text-white-50 text-14 mb-8'>
        Please choose the day(s) for this Timed activity
      </Typography>
      <RadioGroup
        className='pl-3'
        name='message_type'
        value={dayType}
        onChange={handleTypeChange}>
        <Grid container spacing={1}>
          {options.map(option => (
            <Grid item key={option.value} md='auto'>
              <OutlinedRadioControlLabel
                value={option.value}
                label={option.label}
                caption={option.caption}
              />
            </Grid>
          ))}
          {dayType === 'no-end' ? (
            <Grid item xs={12}>
              <Box>
                <Typography variant='body1'>Event Date</Typography>
                <DatePicker
                  disablePast
                  minDate={minDate}
                  value={dates[0]?.start_date ?? null}
                  slots={{openPickerIcon: MuiIcon}}
                  onChange={handleNoEndDate}
                  className='w-250'
                  slotProps={{
                    textField: {size: 'small'},
                    calendarHeader: {className: 'month-year'},
                    popper: {'aria-label': 'Timed message date dialog'},
                  }}
                />
              </Box>
            </Grid>
          ) : null}
          {dayType === 'one-day' ? (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={8}>
                <TimedMsgDateRange
                  onChangeDates={setDateRanges}
                  isOneDay
                  dates={dates}
                />
              </Grid>
            </>
          ) : null}
          {dayType === 'multiple' ? (
            <Grid item xs={12} className='flex w-full justify-end'>
              <TimedMsgDateRange
                onChangeDates={setDateRanges}
                isOneDay={false}
                dates={dates}
              />
            </Grid>
          ) : null}
        </Grid>
      </RadioGroup>
      {dayType !== 'no-end' && dates[0] && dates[0].start_date !== null ? (
        <TableContainer className='my-5 h-220 overflow-auto scrollbar'>
          <Table>
            <TableHead className='blue-variant17'>
              <TableRow>
                {dayType === 'multiple' ? (
                  <>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                  </>
                ) : (
                  <TableCell colSpan={2}>Date</TableCell>
                )}
                {dayType === 'multiple' && (
                  <TableCell align='center'>Days of the week</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {dates.map(range => {
                if (
                  range.start_date &&
                  range.start_date.isValid() &&
                  range.end_date &&
                  range.end_date.isValid()
                ) {
                  return (
                    <TableRow key={range.id}>
                      <TableCell>
                        {range.start_date?.format('MM/DD/YYYY')}
                      </TableCell>
                      {dayType === 'multiple' ? (
                        <TableCell>
                          {range.end_date?.format('MM/DD/YYYY')}
                        </TableCell>
                      ) : null}
                      {dayType === 'multiple' ? (
                        <TableCell>
                          <DayOfTheWeekSelection
                            range={range}
                            setDateRanges={setDateRanges}
                          />
                        </TableCell>
                      ) : null}
                      <TableCell className='text-end'>
                        <IconDeleteButton
                          onClick={() => handleRemove(range.id)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                }
                return null;
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      {isToday &&
        dates &&
        dates.find(x => x.end_date !== null || x.start_date !== null) && (
          <Alert severity='error' className='mt-3'>
            Select one or more days of the week to proceed
          </Alert>
        )}
    </>
  );
};

export default TimedMsgRequestDays;

const DayOfTheWeekSelection = ({
  range,
  setDateRanges,
}: {
  range: iTimedMsgDateRange;
  setDateRanges: React.Dispatch<React.SetStateAction<iTimedMsgDateRange[]>>;
}) => {
  const daysInRange = useMemo(
    () =>
      range.start_date && range.end_date
        ? (GetDaysFromDateRange(range.start_date, range.end_date) as string[])
        : [],
    [range.end_date, range.start_date],
  );

  const handleSelectAllDays = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      const updatedDays = checked ? [...daysInRange] : [];
      setDateRanges(prev =>
        prev.map(dateRange => {
          if (dateRange.id !== range.id) return dateRange;
          const updatedDayTimes = dateRange.day_times?.map(time => ({
            ...time,
            availableDays: updatedDays,
            selectedDays: checked ? [...time.selectedDays] : [],
            timeRanges: checked ? [...time.timeRanges] : [],
          }));
          return {
            ...dateRange,
            chosenDays: updatedDays,
            day_times: updatedDayTimes,
          };
        }),
      );
    },
    [daysInRange, range.id, setDateRanges],
  );

  const handleSelectDay = useCallback(
    (checked: boolean, day: string) => {
      setDateRanges(prev =>
        prev.map(dateRange => {
          if (dateRange.id !== range.id) return dateRange;

          const chosenDays = checked
            ? [...(dateRange.chosenDays || []), day]
            : (dateRange.chosenDays || []).filter(d => d !== day);

          const updatedDayTimes = dateRange.day_times?.map(time => {
            const availableDays = checked
              ? (time.availableDays || []).includes(day)
                ? time.availableDays || []
                : [...(time.availableDays || []), day]
              : (time.availableDays || []).filter(d => d !== day);

            const selectedDays = checked
              ? (time.selectedDays || []).filter(
                  (selectedDay, index, selectedDayValues) =>
                    selectedDayValues.indexOf(selectedDay) === index,
                )
              : (time.selectedDays || []).filter(d => d !== day);

            return {
              ...time,
              availableDays,
              selectedDays,
            };
          });
          return {
            ...dateRange,
            chosenDays,
            day_times: updatedDayTimes,
          };
        }),
      );
    },
    [range.id, setDateRanges],
  );

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            disabled={range.id > 0}
            checked={range.chosenDays?.length === daysInRange.length}
            onChange={handleSelectAllDays}
          />
        }
        label={'All'}
      />
      {daysInRange.map(day => {
        const shortLabel = day.substring(0, 3);
        const isChecked = range.chosenDays?.includes(day) ?? false;
        return (
          <FormControlLabel
            control={
              <Checkbox
                disabled={range.id > 0}
                checked={isChecked}
                onChange={e => handleSelectDay(e.target.checked, day)}
              />
            }
            label={shortLabel}
          />
        );
      })}
    </>
  );
};
